import React from "react";
import { Seeker } from "../../types/seeker";
import {
    ScheduledSession,
    formatSessionInTimezone,
    getSessionStatusLabel,
    fetchSessionsForSeeker,
} from "../../data/sessionService";
import { getSupabaseClient } from "../../lib/supabase";

interface SessionHistoryProps {
    seeker: Seeker;
    sessions: ScheduledSession[];
    loadingSessions: boolean;
    sessionsError: string | null;
}

type SeekerRecord = {
    user_id: string | null;
};

export async function fetchSessionsForSeekerBySeekerId(seekerId: string): Promise<ScheduledSession[]> {
    if (!seekerId) {
        return [];
    }

    const supabase = getSupabaseClient();

    const { data: seekerRecord, error: seekerLookupError } = await supabase
        .from("seekers")
        .select("user_id")
        .eq("seeker_id", seekerId)
        .maybeSingle<SeekerRecord>();

    if (seekerLookupError) {
        console.error("Error looking up seeker user_id:", seekerLookupError);
        throw seekerLookupError;
    }

    const seekerUserId = seekerRecord?.user_id;

    if (!seekerUserId) {
        return [];
    }

    return fetchSessionsForSeeker(seekerUserId);
}

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="rounded-2xl border border-[#2C2731] bg-[#FFFCEF] shadow-[inset_0_0_0_1px_rgba(44,39,49,0.05)]">
        <div className="border-b border-[#2C2731]/20 px-6 pb-4 pt-6">
            <h3 className="text-lg font-semibold text-[#2C2731]">{title}</h3>
        </div>
        <div className="px-6 py-6">{children}</div>
    </div>
);

const StatusBadge: React.FC<{ status: ScheduledSession["session_status"] }> = ({ status }) => {
    const base =
        "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.1em]";
    const classes = (() => {
        switch (status) {
            case "completed":
                return "bg-[#2C2731] text-[#FEF6D6]";
            case "confirmed":
                return "bg-[#403851] text-[#FEF6D6]";
            case "scheduled":
                return "bg-[#6F5E7A] text-[#FEF6D6]";
            case "cancelled":
                return "bg-[#B24E4E] text-[#FEF6D6]";
            case "no_show":
                return "bg-[#8C6B44] text-[#FEF6D6]";
            default:
                return "bg-[#403851] text-[#FEF6D6]";
        }
    })();

    return (
        <span className={`${base} ${classes}`}>{getSessionStatusLabel(status)}</span>
    );
};

export default function SeekerSessionHistory({
    seeker,
    sessions,
    loadingSessions,
    sessionsError,
}: SessionHistoryProps) {
    if (loadingSessions) {
        return (
            <Card title="Session History">
                <div className="flex min-h-[10rem] items-center justify-center text-sm text-[#403851]">
                    Loading sessions…
                </div>
            </Card>
        );
    }

    if (sessionsError) {
        return (
            <Card title="Session History">
                <div className="flex min-h-[10rem] items-center justify-center text-sm text-[#B24E4E]">
                    {sessionsError}
                </div>
            </Card>
        );
    }

    if (!sessions.length) {
        return (
            <Card title="Session History">
                <div className="flex min-h-[10rem] items-center justify-center text-sm text-[#403851]">
                    No sessions recorded yet for <span className="ph-mask">{seeker.name}</span>.
                </div>
            </Card>
        );
    }

    return (
        <Card title="Session History">
            <div className="space-y-3">
                {sessions.map((session) => {
                    const duration = session.duration_minutes ? `${session.duration_minutes} mins` : "Duration unknown";
                    const sessionKey = session.id ?? `${session.session_date}-${session.session_time}`;
                    const { date, time, timezoneAbbrev } = formatSessionInTimezone(session);

                    return (
                        <div
                            key={sessionKey}
                            className="flex items-start justify-between gap-4 rounded-xl border border-[#2C2731]/15 bg-[#FFFCEF] px-4 py-3 shadow-sm"
                        >
                            <div className="flex flex-col gap-1">
                                <div className="text-sm font-semibold text-[#2C2731]">
                                    {session.session_type ?? "Session"}
                                </div>
                                <div className="text-xs text-[#403851]">
                                    {date} · {time}{timezoneAbbrev ? ` ${timezoneAbbrev}` : ''} · {duration}
                                </div>
                                {session.notes && (
                                    <div className="text-xs text-[#403851]/80 ph-mask">Notes: {session.notes}</div>
                                )}
                            </div>
                            <StatusBadge status={session.session_status ?? "scheduled"} />
                        </div>
                    );
                })}
            </div>
        </Card>
    );
}
